import { downloadFromUrls } from "~/services/downloader";
import { PDF_TOPICS, STORAGE_KEYS } from "~/types/Topic";

/**
 * Batch ids for which the async PDF export has already been resolved (files
 * downloaded), whichever channel got there first: the Mercure
 * `pdf.generation.success` event handled below, or the polling fallback in
 * usePdfExporter (see OCV-Infrastructure#102: the Mercure hub can silently
 * drop this event after being recreated). Shared at module scope so both
 * channels can check/mark it and only one of them ever acts on a given batch.
 */
export const processedPdfBatchIds = new Set<string>();

/**
 * Batch ids currently awaited by the polling fallback in usePdfExporter.
 */
export const pendingPdfBatchIds = new Set<string>();

/**
 * Single-file exports this tab awaits, from their request on. The PDF service
 * announces such an export to every tab of the user with a success event that
 * carries no batch id, possibly before the request returns that id: the event
 * can only be matched to one of these. In the sets of this module, a
 * single-file batch goes by the id of its export.
 */
export const pendingSingleFileExportIds = new Set<string>();

/**
 * Batch ids whose files one channel is downloading: the other channel leaves
 * them alone until that download succeeds (processed) or fails (retried).
 */
export const downloadingPdfBatchIds = new Set<string>();

let singleFileExportCount = 0;

/**
 * Starts awaiting a single-file export as its request is sent, and returns
 * the id it goes by.
 */
export function trackSingleFileExport(): string {
  singleFileExportCount += 1;
  const exportId = `single-file-export-${singleFileExportCount}`;
  pendingSingleFileExportIds.add(exportId);
  return exportId;
}

/**
 * Records a batch as resolved once its files are downloaded, whether or not
 * the polling fallback awaits it yet: a later registration then skips it.
 */
export function markPdfBatchProcessed(batchId: string): void {
  pendingPdfBatchIds.delete(batchId);
  pendingSingleFileExportIds.delete(batchId);
  processedPdfBatchIds.add(batchId);
}

/**
 * The batch a Mercure success event refers to. An event without batch id
 * comes from a single-file export: it is matched to the oldest single-file
 * export this tab still awaits, or to none, never to a batch of another kind.
 * The event identifies no export, so the polling fallback checks the match
 * against the batch status when it can (see pollPdfBatchStatus).
 */
export function batchIdOfMercureSuccess(batchId?: string): string | undefined {
  if (batchId) {
    return batchId;
  }

  for (const id of pendingSingleFileExportIds) {
    if (!downloadingPdfBatchIds.has(id)) {
      return id;
    }
  }

  return undefined;
}

export const usePdfNotifications = () => {
  const mercure = useMercure();
  const config = useRuntimeConfig();
  const { $eventBus } = useNuxtApp();
  const setupNotificationSources = async (user) => {
    // A failed authorization does not prevent the connection: its recovery authorizes again.
    // Only a lost Backoffice session leaves it out (createEventSource then returns null).
    await mercure.reauthorize();

    const pdfGenerationSource = mercure.createEventSource(
      `${config.public.sseUrl}`,
      [PDF_TOPICS.user(user.global_id)],
      [PDF_TOPICS.user(user.global_id)],
      STORAGE_KEYS.PDF_GENERATION,
      "de génération PDF",
    );

    return { pdfGenerationSource };
  };
  function showSuccessToast(summary, group, life) {
    $eventBus.emit("toast", {
      severity: "success",
      unique: true,
      group,
      summary,
      life,
    });
  }

  function showErrorToast(summary, group, life = 5000, detail) {
    $eventBus.emit("toast", {
      severity: "error",
      unique: true,
      group,
      summary,
      ...(detail && { detail }),
      life,
    });
  }

  function notifyEmailDeliveryStatus() {
    $eventBus.emit("email-deliver-status", {});
  }
  const EVENT_HANDLERS = {
    [PDF_TOPICS.GENERATION.SUCCESS]: (event) => {
      const batchId = batchIdOfMercureSuccess(event.data.data?.batch_id);

      // The polling fallback already downloaded this exact batch, or is
      // downloading it: acting on this (delayed) event would download and
      // toast the same export twice.
      if (
        batchId
        && (processedPdfBatchIds.has(batchId) || downloadingPdfBatchIds.has(batchId))
      ) {
        return;
      }

      showSuccessToast(
        "Export des factures en pdf réussi",
        "export-pdf-invoice-status",
        3000,
      );

      if (!(event.data.data?.urls?.length > 0)) {
        if (batchId) {
          markPdfBatchProcessed(batchId);
        }
        return;
      }

      if (batchId) {
        downloadingPdfBatchIds.add(batchId);
      }
      onNuxtReady(async () => {
        try {
          await downloadFromUrls(event.data.data);
          if (batchId) {
            markPdfBatchProcessed(batchId);
          }
        }
        catch (error) {
          // Left unresolved: the polling fallback, when it awaits this
          // batch, retries the download or reports the failure.
          console.error(error);
        }
        finally {
          if (batchId) {
            downloadingPdfBatchIds.delete(batchId);
          }
        }
      });
    },

    [PDF_TOPICS.GENERATION.FAILED]: (event) => {
      showErrorToast(
        event.data.message || "Erreur de génération PDF",
        "export-pdf-invoice-status",
        10000,
        undefined,
      );
    },

    [PDF_TOPICS.EMAIL_DELIVERY.SUCCESS]: (event) => {
      showSuccessToast(
        event.data.message || "Email de PDF envoyé avec succès",
        "export-pdf-email-delivery-status",
        3000,
      );
      notifyEmailDeliveryStatus();
    },

    [PDF_TOPICS.EMAIL_DELIVERY.FAILED_PARTIALLY]: (event) => {
      showErrorToast(
        "erreur",
        null,
        undefined,
        event.data.reason || "Certains emails n'ont pas pu être envoyés",
      );
      notifyEmailDeliveryStatus();
    },

    [PDF_TOPICS.EMAIL_DELIVERY.FAILED]: (event) => {
      showErrorToast(
        "erreur",
        "export-pdf-email-delivery-status",
        undefined,
        event.data.reason || "Impossible d'envoyer les emails",
      );
      notifyEmailDeliveryStatus();
    },
  };
  const setupEventHandlers = (pdfGenerationSource) => {
    // Handle PDF generation events
    watch(
      [pdfGenerationSource.data, pdfGenerationSource.event],
      ([event, _event]) => {
        const rawEvent = toValue(event);

        if (!rawEvent) return;

        const pdfStreamEvent = JSON.parse(rawEvent);
        const handler = EVENT_HANDLERS[pdfStreamEvent.event];
        if (handler) {
          handler(pdfStreamEvent);
        }
      },
    );
  };

  return {
    setupNotificationSources,
    setupEventHandlers,
  };
};
